export interface SurpriseFact {
  id: string;
  title: string;
  fact: string;
  comparison?: { item1: { name: string; gi: number }; item2: { name: string; gi: number } };
  emoji: string;
  shareText: string;
  category: "comparison" | "myth-buster" | "science" | "cooking-hack";
}

export const SURPRISE_FACTS: SurpriseFact[] = [
  {
    id: "chocolate-banana",
    title: "Dark chocolate vs banana",
    fact: "Dark chocolate (GI 23) has a lower glycemic index than a banana (GI 51). The fat content slows absorption.",
    comparison: { item1: { name: "Dark Chocolate", gi: 23 }, item2: { name: "Banana", gi: 51 } },
    emoji: "",
    shareText: "Dark chocolate has a lower GI than a banana. The fat content slows glucose absorption.",
    category: "comparison",
  },
  {
    id: "watermelon-snickers",
    title: "Watermelon vs Snickers",
    fact: "Watermelon (GI 76) produces a larger blood sugar spike than a Snickers bar (GI 55). The fat and protein in the candy bar slow digestion.",
    comparison: { item1: { name: "Watermelon", gi: 76 }, item2: { name: "Snickers", gi: 55 } },
    emoji: "",
    shareText: "Watermelon has a higher GI than a Snickers bar. Fat and protein in the candy slow digestion.",
    category: "comparison",
  },
  {
    id: "carrots-myth",
    title: "The carrot myth",
    fact: "Carrots were incorrectly classified as high-GI for years due to a calculation error. Their actual GI is 16.",
    emoji: "",
    shareText: "Carrots have a GI of only 16. The long-standing high-GI label originated from a calculation error.",
    category: "myth-buster",
  },
  {
    id: "pizza-bread",
    title: "Pizza vs white bread",
    fact: "Pizza (GI 60) produces a smaller glucose response than plain white bread (GI 75). Fat and toppings slow carbohydrate absorption.",
    comparison: { item1: { name: "Pizza", gi: 60 }, item2: { name: "White Bread", gi: 75 } },
    emoji: "",
    shareText: "Pizza has a lower GI than white bread. The fat and toppings slow carbohydrate absorption.",
    category: "comparison",
  },
  {
    id: "popcorn-rice",
    title: "Popcorn vs white rice",
    fact: "Popcorn (GI 65) ranks lower than white rice (GI 73). As a whole grain, it retains more fiber and structure.",
    comparison: { item1: { name: "Popcorn", gi: 65 }, item2: { name: "White Rice", gi: 73 } },
    emoji: "",
    shareText: "Popcorn has a lower GI than white rice. Its whole-grain structure retains more fiber.",
    category: "comparison",
  },
  {
    id: "ice-cream",
    title: "Ice cream and fat content",
    fact: "Ice cream (GI 51) falls in the medium-GI range. Its fat content slows the rate of sugar absorption.",
    emoji: "",
    shareText: "Ice cream has a moderate GI of 51. Its fat content slows sugar absorption.",
    category: "myth-buster",
  },
  {
    id: "pasta-al-dente",
    title: "Al dente vs overcooked pasta",
    fact: "Al dente pasta (GI 45) has a meaningfully lower glycemic index than overcooked pasta (GI 55). Longer cooking breaks down starch structure.",
    comparison: { item1: { name: "Al Dente Pasta", gi: 45 }, item2: { name: "Overcooked Pasta", gi: 55 } },
    emoji: "",
    shareText: "Al dente pasta has a GI 10 points lower than overcooked. Cooking time affects starch structure.",
    category: "cooking-hack",
  },
  {
    id: "potato-cooled",
    title: "Cooled potatoes and resistant starch",
    fact: "Cooling potatoes after cooking converts some starch into resistant starch, lowering the glycemic response by up to 25%.",
    emoji: "",
    shareText: "Cooling cooked potatoes creates resistant starch, reducing glycemic response by up to 25%.",
    category: "cooking-hack",
  },
  {
    id: "sourdough-secret",
    title: "Sourdough vs white bread",
    fact: "Sourdough (GI 54) has a substantially lower glycemic index than regular white bread (GI 75). The fermentation process partially breaks down starches before consumption.",
    comparison: { item1: { name: "Sourdough", gi: 54 }, item2: { name: "White Bread", gi: 75 } },
    emoji: "",
    shareText: "Sourdough fermentation lowers its GI by over 20 points compared to white bread.",
    category: "science",
  },
  {
    id: "dates-glucose",
    title: "Dates vs pure glucose",
    fact: "Dates (GI 103) raise blood sugar faster than pure glucose (GI 100). A natural origin does not guarantee a low glycemic index.",
    comparison: { item1: { name: "Dates", gi: 103 }, item2: { name: "Glucose", gi: 100 } },
    emoji: "",
    shareText: "Dates have a higher GI than pure glucose. Natural origin does not guarantee a low glycemic index.",
    category: "comparison",
  },
  {
    id: "vinegar-hack",
    title: "Vinegar and glucose response",
    fact: "Adding vinegar or lemon juice to a meal can reduce the postprandial blood sugar response by up to 30%. The acetic acid slows gastric emptying.",
    emoji: "",
    shareText: "Vinegar or lemon juice can reduce a meal's blood sugar response by up to 30% via slower gastric emptying.",
    category: "cooking-hack",
  },
  {
    id: "green-banana",
    title: "Banana ripeness and GI",
    fact: "A green banana (GI 30) has a markedly lower glycemic index than a ripe banana (GI 51). As bananas ripen, resistant starch converts to sugar.",
    comparison: { item1: { name: "Green Banana", gi: 30 }, item2: { name: "Ripe Banana", gi: 51 } },
    emoji: "",
    shareText: "Green bananas have a GI of 30; ripe bananas rise to 51 as resistant starch converts to sugar.",
    category: "science",
  },
  {
    id: "hummus-hero",
    title: "Hummus at GI 6",
    fact: "Hummus has a glycemic index of just 6, placing it among the lowest-GI foods available. The combination of chickpea fiber, fat, and protein minimizes glucose impact.",
    emoji: "",
    shareText: "Hummus has a GI of 6. Its combination of fiber, fat, and protein minimizes glucose impact.",
    category: "myth-buster",
  },
  {
    id: "protein-first",
    title: "Meal order and glucose response",
    fact: "Eating protein before carbohydrates can reduce the resulting glucose spike by up to 40%. Protein stimulates insulin and slows gastric emptying.",
    emoji: "",
    shareText: "Eating protein before carbs can reduce glucose spikes by up to 40% via slower gastric emptying.",
    category: "science",
  },
  {
    id: "oats-types",
    title: "Steel-cut vs instant oats",
    fact: "Steel-cut oats (GI 42) have roughly half the glycemic index of instant oats (GI 79). Greater processing exposes more starch surface area to digestion.",
    comparison: { item1: { name: "Steel-Cut Oats", gi: 42 }, item2: { name: "Instant Oats", gi: 79 } },
    emoji: "",
    shareText: "Steel-cut oats have roughly half the GI of instant oats. Processing increases starch exposure.",
    category: "comparison",
  },
  {
    id: "peanuts-low",
    title: "Peanuts at GI 14",
    fact: "Peanuts have a glycemic index of 14, lower than most vegetables. Their high fat and protein content offsets their modest carbohydrate load.",
    emoji: "",
    shareText: "Peanuts have a GI of 14, lower than most vegetables. Fat and protein offset their carbohydrate content.",
    category: "myth-buster",
  },
  {
    id: "apple-juice",
    title: "Whole apple vs apple juice",
    fact: "A whole apple (GI 36) has a lower glycemic index than apple juice (GI 41). The intact fiber matrix slows sugar absorption.",
    comparison: { item1: { name: "Whole Apple", gi: 36 }, item2: { name: "Apple Juice", gi: 41 } },
    emoji: "",
    shareText: "Whole apples have a lower GI than apple juice. Intact fiber slows sugar absorption.",
    category: "science",
  },
  {
    id: "walk-after",
    title: "Post-meal walking",
    fact: "A 10-minute walk after eating can reduce the blood sugar spike by approximately 22%. Muscle contraction increases glucose uptake independent of insulin.",
    emoji: "",
    shareText: "A 10-minute post-meal walk reduces blood sugar spikes by about 22% through increased muscle glucose uptake.",
    category: "science",
  },
  {
    id: "basmati-vs-jasmine",
    title: "Basmati vs jasmine rice",
    fact: "Basmati rice (GI 58) has a substantially lower glycemic index than jasmine rice (GI 89). Basmati's higher amylose content slows digestion.",
    comparison: { item1: { name: "Basmati Rice", gi: 58 }, item2: { name: "Jasmine Rice", gi: 89 } },
    emoji: "",
    shareText: "Basmati rice has a GI 31 points lower than jasmine rice, due to its higher amylose content.",
    category: "comparison",
  },
  {
    id: "sweet-potato",
    title: "Sweet potato vs baked potato",
    fact: "A boiled sweet potato (GI 44) has roughly half the glycemic index of a baked regular potato (GI 85). Cooking method and variety both matter.",
    comparison: { item1: { name: "Boiled Sweet Potato", gi: 44 }, item2: { name: "Baked Potato", gi: 85 } },
    emoji: "",
    shareText: "Boiled sweet potatoes have roughly half the GI of baked regular potatoes. Method and variety both matter.",
    category: "comparison",
  },
  {
    id: "cheese-zero",
    title: "Cheese at GI 0",
    fact: "Most cheeses have a glycemic index of 0. With negligible carbohydrate content, they produce no measurable blood sugar response.",
    emoji: "",
    shareText: "Most cheeses have a GI of 0. With negligible carbohydrates, they produce no measurable glucose response.",
    category: "myth-buster",
  },
  {
    id: "cinnamon-magic",
    title: "Cinnamon and insulin sensitivity",
    fact: "Adding cinnamon to meals may improve insulin sensitivity by up to 20%. Cinnamaldehyde appears to enhance cellular glucose uptake.",
    emoji: "",
    shareText: "Cinnamon may improve insulin sensitivity by up to 20%. Cinnamaldehyde enhances cellular glucose uptake.",
    category: "cooking-hack",
  },
];

export function getDailyFact(): SurpriseFact {
  const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86400000);
  // Use a different offset than daily tips to show variety
  return SURPRISE_FACTS[(dayOfYear + 7) % SURPRISE_FACTS.length];
}
